import classNames from 'classnames';
import { FullPoll, PollOption } from 'model';
import { useCallback, useState } from 'react';
import {
	useCreatePollResponseMutation,
	useDeletePollResponseMutation,
	useGetPollResponseCountQuery,
	useGetUserPollResponseQuery,
	useUpdatePollResponseMutation
} from '../../api/PollResponseAPI';
import { PollRadioButton } from '../Forms/PollRadioButton';
import styles from '../PollContent.module.css';
import { PostTypeViewProps } from './types.js';
// eslint-disable-next-line css-modules/no-unused-class
import postStyles from '../../styles/posts.module.css';
import { filterInlineImages } from '../../util/filterInlineImages.js';
import { BigImageContainer } from '../BigImageContainer.js';
import { PostContentsToHTML } from '../PostContentsToHTML.js';
import { PostHeader } from '../PostHeader.js';
import { PostTitle } from '../PostTitle.js';

export function PollView({ post: poll, preview, detail, testId }: PostTypeViewProps<FullPoll>) {
	const {
		data: userVoted,
		isError: userPollResponseError,
		isFetching: userPollResponseFetching
	} = useGetUserPollResponseQuery(poll._id);
	const [selectedOption, setSelectedOption] = useState<string | undefined>();
	const { data: pollCount } = useGetPollResponseCountQuery(poll._id);
	const [createPollResponse] = useCreatePollResponseMutation();
	const [deletePollResponse] = useDeletePollResponseMutation();
	const [updatePollResponse] = useUpdatePollResponseMutation();
	const usersVote = userVoted && !userPollResponseError ? userVoted : undefined;
	const calculatePercentage = (votes: number[]): number[] => {
		const totalVotes = votes.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
		const percentages = votes.map(vote =>
			Math.round(isNaN((vote / totalVotes) * 100) ? 0 : (vote / totalVotes) * 100)
		);
		return percentages;
	};
	const compareObjectsAndReturnValues = useCallback((): number[] => {
		const valuesArray: number[] = [];
		poll.options.forEach((obj: PollOption) => {
			const key = obj.key;

			// eslint-disable-next-line no-prototype-builtins
			if (pollCount?.options.hasOwnProperty(key)) {
				valuesArray.push(pollCount?.options[key]);
			} else {
				valuesArray.push(0);
			}
		});

		return valuesArray;
	}, [poll.options, pollCount?.options]);

	const percentages: number[] = calculatePercentage(compareObjectsAndReturnValues());

	const pollVoted = (option: PollOption) => {
		if (usersVote?.option.key === option.key) {
			setSelectedOption('unselected');
			deletePollResponse(poll._id);
		} else {
			setSelectedOption(option.key);
			usersVote !== undefined && !userPollResponseError
				? updatePollResponse({ poll: poll._id, option })
				: createPollResponse({ poll: poll._id, option });
		}
	};

	return (
		<>
			<PostHeader post={poll} />
			{!preview && detail && poll.images.length > 0 && (
				<BigImageContainer post={poll} images={filterInlineImages(poll.contents, poll.images)} />
			)}
			<div className={postStyles.postHeader}>
				<PostTitle postId={poll._id} className={postStyles.pollQuestion}>
					<PostContentsToHTML>{poll?.contents}</PostContentsToHTML>
				</PostTitle>
				{!preview && (
					<div
						className={classNames(
							styles.pollOptionsContainer,
							userPollResponseFetching && styles.loadingOpacity
						)}
						data-testid={testId}
					>
						{poll.options.map((option: PollOption, i: number) => (
							<div key={i} className={styles.pollOptionContainer} onClick={() => pollVoted(option)}>
								<div className={styles.pollRadio}>
									<PollRadioButton
										checked={
											selectedOption
												? selectedOption === option.key
												: usersVote?.option.key === option.key
										}
									/>
								</div>
								<div className={styles.pollOptionOuter}>
									<div className={styles.pollOption} data-testid={`Poll-${poll._id}-Option-${i}`}>
										{option.value}
									</div>
									<div
										className={styles.pollOptionProgress}
										style={{
											width: `${
												usersVote !== undefined && !userPollResponseError ? percentages[i] : 0
											}%`
										}}
									/>
								</div>
								<div style={{ width: usersVote ? '50px' : '0px' }} className={styles.percentage}>
									{percentages[i]}%
								</div>
							</div>
						))}
					</div>
				)}
			</div>
			{!preview && !detail && poll.images.length > 0 && (
				<BigImageContainer post={poll} images={filterInlineImages(poll.contents, poll.images)} />
			)}
		</>
	);
}
